import React from 'react';
import {
    ListItem,
    ListItemSecondaryAction,
    IconButton,
    Avatar,
    Box,
    Typography,
    Chip,
    Tooltip,
    LinearProgress
} from '@mui/material';
import { Visibility, Edit, Translate, MoreVert, CheckCircle, Warning } from '@mui/icons-material';
import PropTypes from 'prop-types';

const ShortStoryListItem = ({ 
    story, 
    orderNumber, 
    onView, 
    onEdit, 
    onTranslate, 
    onMenuOpen 
}) => {
    if (!story) {
        return null;
    }

    return (
        <ListItem sx={{ border: 1, borderColor: 'divider', mb: 1, borderRadius: 1 }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minWidth: 40,
                mr: 2,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: '50%',
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                fontWeight: 'bold'
            }}>
                {orderNumber}
            </Box>
            <Avatar src={story.thumbUrl} variant="square" sx={{ margin: 2, width: 56, height: 56 }} />
            <Box sx={{ flexGrow: 1, ml: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" component="div">
                        {story.title}
                    </Typography>
                    <Tooltip title={story.isReadyToServe ? "Ready to serve to users" : "Not ready to serve"}>
                        {story.isReadyToServe ? (
                            <CheckCircle color="success" fontSize="small" />
                        ) : (
                            <Warning color="warning" fontSize="small" />
                        )}
                    </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    by {story.author} • {story.language?.name}
                    {story.level?.name && ` • ${story.level.name}`}
                    {story.isReadyToServe ? (
                        <Chip 
                            label="Ready" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                        />
                    ) : (
                        <Chip 
                            label="Draft" 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                        />
                    )}
                </Typography>
                {story.sourceTitle && (
                    <Typography variant="body2" color="text.secondary">
                        Source: {story.sourceTitle}
                    </Typography>
                )}
                
                {/* Translation Progress */}
                {story.translationProgress && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Translation Progress
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {story.translationProgress.averageProgress}% 
                                ({story.translationProgress.totalTranslations} {story.translationProgress.totalTranslations === 1 ? 'translation' : 'translations'})
                            </Typography>
                        </Box>
                        <Tooltip 
                            title={
                                story.translationProgress.totalTranslations === 0 
                                    ? "No translations available" 
                                    : `Average progress across ${story.translationProgress.totalTranslations} translation(s)`
                            }
                        >
                            <LinearProgress 
                                variant="determinate" 
                                value={story.translationProgress.averageProgress}
                                sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        backgroundColor: story.translationProgress.averageProgress === 0 
                                            ? '#f44336' 
                                            : story.translationProgress.averageProgress < 50 
                                                ? '#ff9800' 
                                                : story.translationProgress.averageProgress < 100 
                                                    ? '#2196f3' 
                                                    : '#4caf50'
                                    }
                                }}
                            />
                        </Tooltip>
                    </Box>
                )}
                
                <Box sx={{ mt: 1 }}>
                    {story.tags && story.tags.map((tag, index) => (
                        <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    ))}
                </Box>
            </Box>
            <ListItemSecondaryAction>
                <IconButton
                    edge="end"
                    aria-label="view"
                    onClick={() => onView(story)}
                    sx={{ mr: 1 }}
                >
                    <Visibility />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="translate"
                    onClick={() => onTranslate(story)}
                    sx={{ mr: 1 }}
                    color="primary"
                >
                    <Translate />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => onEdit(story)}
                    sx={{ mr: 1 }}
                >
                    <Edit />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => onMenuOpen(e, story)}
                >
                    <MoreVert />
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    );
};

ShortStoryListItem.propTypes = {
    story: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        author: PropTypes.string.isRequired,
        thumbUrl: PropTypes.string,
        isReadyToServe: PropTypes.bool,
        sourceTitle: PropTypes.string,
        language: PropTypes.shape({
            name: PropTypes.string
        }),
        level: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string
        }),
        tags: PropTypes.arrayOf(PropTypes.string),
        translationProgress: PropTypes.shape({
            averageProgress: PropTypes.number,
            totalTranslations: PropTypes.number
        }),
        text: PropTypes.shape({
            _id: PropTypes.string
        })
    }).isRequired,
    orderNumber: PropTypes.number.isRequired,
    onView: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onTranslate: PropTypes.func.isRequired,
    onMenuOpen: PropTypes.func.isRequired
};

export default ShortStoryListItem; 